import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Search, Trash2, Users, X } from 'lucide-react';
import { API_BASE, authFetch } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';

function ClientManagerModal({ onClose }) {
  const [growers, setGrowers] = useState([]);
  const [filteredGrowers, setFilteredGrowers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrower, setSelectedGrower] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllGrowers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredGrowers(growers);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = growers.filter(
        (g) =>
          g.grower_name.toLowerCase().includes(term) ||
          g.contact_person?.toLowerCase().includes(term)
      );
      setFilteredGrowers(filtered);
    }
  }, [searchTerm, growers]);

  const fetchAllGrowers = async () => {
    setLoading(true);
    try {
      // Fetch growers for current user's company only
      // This ensures admin users only see their own clients
      // and non-admin users also only see their own clients
      const growersRes = await authFetch(`${API_BASE}/growers/current-user/all`);
      if (!growersRes.ok) {
        console.error('Failed to fetch growers');
        setGrowers([]);
        setFilteredGrowers([]);
        return;
      }
      const growers = await growersRes.json();

      setGrowers(growers);
      setFilteredGrowers(growers);
    } catch (err) {
      console.error('Failed to fetch growers:', err);
      setGrowers([]);
      setFilteredGrowers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedGrower(null);
    setShowAddForm(true);
  };

  const handleSelectGrower = (grower) => {
    setSelectedGrower(grower);
    setShowAddForm(true);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setSelectedGrower(null);
    fetchAllGrowers();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full h-[90vh] flex flex-col">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Client Manager</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="w-80 border-r flex flex-col bg-gray-50">
            <div className="p-4 space-y-3 border-b bg-white">
              <button
                onClick={handleAddNew}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                <Plus size={18} />
                Add Client
              </button>

              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search clients..."
                  className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading clients...</div>
              ) : filteredGrowers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchTerm ? 'No clients found' : 'No clients yet. Click "Add Client" to get started.'}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredGrowers.map((grower) => (
                    <div
                      key={grower.id}
                      onClick={() => handleSelectGrower(grower)}
                      className={`p-4 cursor-pointer hover:bg-white transition ${
                        selectedGrower?.id === grower.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                      }`}
                    >
                      <div className="font-medium text-gray-900">{grower.grower_name}</div>
                      {grower.contact_person && (
                        <div className="text-sm text-gray-600 mt-1">{grower.contact_person}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center bg-gray-50">
            {!showAddForm ? (
              <div className="text-center text-gray-500 p-8">
                <Users size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-lg">Select a client to view details</p>
                <p className="text-sm mt-2">or click "Add Client" to create a new one</p>
              </div>
            ) : (
              <ClientForm grower={selectedGrower} onClose={handleCloseForm} onSave={handleCloseForm} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientForm({ grower, onClose, onSave }) {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [farms, setFarms] = useState([]);
  const [fields, setFields] = useState([]);

  const [growerName, setGrowerName] = useState(grower?.grower_name || '');
  const [contactPerson, setContactPerson] = useState(grower?.contact_person || '');
  const [email, setEmail] = useState(grower?.email || '');
  const [phone, setPhone] = useState(grower?.phone || '');
  const [address, setAddress] = useState(grower?.address || '');
  const [city, setCity] = useState(grower?.city || '');
  const [state, setState] = useState(grower?.state || '');
  const [zip, setZip] = useState(grower?.zip || '');
  const [notes, setNotes] = useState(grower?.notes || '');

  // Update form fields when grower changes
  useEffect(() => {
    setGrowerName(grower?.grower_name || '');
    setContactPerson(grower?.contact_person || '');
    setEmail(grower?.email || '');
    setPhone(grower?.phone || '');
    setAddress(grower?.address || '');
    setCity(grower?.city || '');
    setState(grower?.state || '');
    setZip(grower?.zip || '');
    setNotes(grower?.notes || '');
  }, [grower]);

  const [showAddFarm, setShowAddFarm] = useState(false);
  const [newFarmName, setNewFarmName] = useState('');
  const [newFarmLocation, setNewFarmLocation] = useState('');
  const [newFarmAcres, setNewFarmAcres] = useState('');

  const [showAddField, setShowAddField] = useState(false);
  const [newFieldFarmId, setNewFieldFarmId] = useState('');
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldAcres, setNewFieldAcres] = useState('');
  const [newFieldLat, setNewFieldLat] = useState('');
  const [newFieldLong, setNewFieldLong] = useState('');

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      if (grower) {
        const farmsRes = await authFetch(`${API_BASE}/farms/grower/${grower.id}`);
        const farmsData = await farmsRes.json();
        setFarms(farmsData);

        let allFields = [];
        for (const farm of farmsData) {
          const fieldsRes = await authFetch(`${API_BASE}/fields/farm/${farm.id}`);
          const fieldsData = await fieldsRes.json();
          allFields = [
            ...allFields,
            ...fieldsData.map((f) => ({
              ...f,
              farm_name: farm.farm_name
            }))
          ];
        }
        setFields(allFields);
      } else {
        setFarms([]);
        setFields([]);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [grower]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleSave = async () => {
    if (!growerName) {
      alert('Please fill in Grower Name');
      return;
    }

    try {
      // All users (including admins) add clients to their own company
      // Since admin is now part of KAS Internal, they will add clients to KAS Internal
      if (!user?.company_id) {
        alert('User company not found. Please contact support.');
        return;
      }
      
      const companyId = user.company_id;

      const payload = {
        company_id: companyId,
        grower_name: growerName,
        contact_person: contactPerson,
        email,
        phone,
        address,
        city,
        state,
        zip,
        notes
      };

      if (grower) {
        // When updating, preserve the original company_id (don't allow changing it)
        const updatePayload = {
          ...payload,
          company_id: grower.company_id
        };
        
        const res = await authFetch(`${API_BASE}/growers/${grower.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatePayload)
        });

        if (res.ok) {
          alert('Client updated successfully!');
          onSave();
        } else {
          alert('Failed to update client');
        }
      } else {
        const res = await authFetch(`${API_BASE}/growers/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          alert('Client added successfully!');
          onSave();
        } else {
          alert('Failed to add client');
        }
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleAddFarm = async () => {
    if (!newFarmName) {
      alert('Please enter a farm name');
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/farms/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grower_id: grower.id,
          farm_name: newFarmName,
          location: newFarmLocation,
          total_acres: newFarmAcres ? parseFloat(newFarmAcres) : null
        })
      });

      if (res.ok) {
        setNewFarmName('');
        setNewFarmLocation('');
        setNewFarmAcres('');
        setShowAddFarm(false);
        fetchInitialData();
      } else {
        alert('Failed to add farm');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeleteFarm = async (farmId) => {
    if (!window.confirm('Delete this farm? This will also delete all its fields.')) {
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/farms/${farmId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        fetchInitialData();
      } else {
        alert('Failed to delete farm');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleAddField = async () => {
    if (!newFieldFarmId || !newFieldName) {
      alert('Please select a farm and enter a field name');
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/fields/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          farm_id: parseInt(newFieldFarmId, 10),
          field_name: newFieldName,
          acres: newFieldAcres ? parseFloat(newFieldAcres) : null,
          latitude: newFieldLat ? parseFloat(newFieldLat) : null,
          longitude: newFieldLong ? parseFloat(newFieldLong) : null
        })
      });

      if (res.ok) {
        setNewFieldFarmId('');
        setNewFieldName('');
        setNewFieldAcres('');
        setNewFieldLat('');
        setNewFieldLong('');
        setShowAddField(false);
        fetchInitialData();
      } else {
        alert('Failed to add field');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Delete this field?')) {
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/fields/${fieldId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        fetchInitialData();
      } else {
        alert('Failed to delete field');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!grower) {
      onClose();
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${growerName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/growers/${grower.id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        alert('Client deleted successfully!');
        onSave();
      } else {
        alert('Failed to delete client');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 w-full h-full flex items-center justify-center">
        <div className="text-gray-500">Loading client details...</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto p-8 bg-white">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Grower Name *</label>
              <input
                type="text"
                value={growerName}
                onChange={(e) => setGrowerName(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Enter grower name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
              <input
                type="text"
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Contact name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="email@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Street address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="City"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="State"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ZIP</label>
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="ZIP code"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                rows="3"
                placeholder="Any additional notes..."
              />
            </div>
          </div>
        </div>

        {grower && (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-700">Farms ({farms.length})</h4>
                <button
                  onClick={() => setShowAddFarm(!showAddFarm)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  <Plus size={16} />
                  Add Farm
                </button>
              </div>

              {showAddFarm && (
                <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      value={newFarmName}
                      onChange={(e) => setNewFarmName(e.target.value)}
                      className="border rounded px-3 py-2"
                      placeholder="Farm name *"
                    />
                    <input
                      type="text"
                      value={newFarmLocation}
                      onChange={(e) => setNewFarmLocation(e.target.value)}
                      className="border rounded px-3 py-2"
                      placeholder="Location"
                    />
                    <input
                      type="number"
                      value={newFarmAcres}
                      onChange={(e) => setNewFarmAcres(e.target.value)}
                      className="border rounded px-3 py-2"
                      placeholder="Total acres"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleAddFarm}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      Save Farm
                    </button>
                    <button
                      onClick={() => {
                        setShowAddFarm(false);
                        setNewFarmName('');
                        setNewFarmLocation('');
                        setNewFarmAcres('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {farms.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">No farms added yet</div>
              ) : (
                <div className="space-y-2">
                  {farms.map((farm) => (
                    <div
                      key={farm.id}
                      className="border rounded-lg p-4 bg-gray-50 flex justify-between items-start"
                    >
                      <div>
                        <div className="font-medium">{farm.farm_name}</div>
                        {farm.location && <div className="text-sm text-gray-600">{farm.location}</div>}
                        {farm.total_acres && <div className="text-sm text-gray-500">{farm.total_acres} acres</div>}
                      </div>
                      <button onClick={() => handleDeleteFarm(farm.id)} className="text-red-600 hover:text-red-700 ml-4">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-700">Fields ({fields.length})</h4>
                <button
                  onClick={() => setShowAddField(!showAddField)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  disabled={farms.length === 0}
                >
                  <Plus size={16} />
                  Add Field
                </button>
              </div>

              {showAddField && (
                <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <select
                      value={newFieldFarmId}
                      onChange={(e) => setNewFieldFarmId(e.target.value)}
                      className="border rounded px-3 py-2"
                    >
                      <option value="">Select farm *</option>
                      {farms.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.farm_name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      className="border rounded px-3 py-2"
                      placeholder="Field name *"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="number"
                      value={newFieldAcres}
                      onChange={(e) => setNewFieldAcres(e.target.value)}
                      className="border rounded px-3 py-2"
                      placeholder="Acres"
                    />
                    <input
                      type="number"
                      step="0.000001"
                      value={newFieldLat}
                      onChange={(e) => setNewFieldLat(e.target.value)}
                      className="border rounded px-3 py-2"
                      placeholder="Latitude (optional)"
                    />
                    <input
                      type="number"
                      step="0.000001"
                      value={newFieldLong}
                      onChange={(e) => setNewFieldLong(e.target.value)}
                      className="border rounded px-3 py-2"
                      placeholder="Longitude (optional)"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleAddField}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      Save Field
                    </button>
                    <button
                      onClick={() => {
                        setShowAddField(false);
                        setNewFieldFarmId('');
                        setNewFieldName('');
                        setNewFieldAcres('');
                        setNewFieldLat('');
                        setNewFieldLong('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {fields.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">No fields added yet</div>
              ) : (
                <div className="space-y-2">
                  {fields.map((field) => (
                    <div
                      key={field.id}
                      className="border rounded-lg p-3 bg-gray-50 flex justify-between items-center"
                    >
                      <div>
                        <span className="font-medium">{field.field_name}</span>
                        <span className="text-sm text-gray-600 ml-2">({field.farm_name})</span>
                        {field.acres && <span className="text-sm text-gray-500 ml-2">• {field.acres} ac</span>}
                        {(field.latitude || field.longitude) && (
                          <span className="text-xs text-gray-400 ml-2">
                            • {field.latitude || '-'}, {field.longitude || '-'}
                          </span>
                        )}
                      </div>
                      <button onClick={() => handleDeleteField(field.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-8 border-t mt-8">
        {grower && (
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <Trash2 size={18} />
            Delete Client
          </button>
        )}

        <div className="flex gap-3 ml-auto">
          <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Cancel
          </button>
          <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Save Client
          </button>
        </div>
      </div>
    </div>
  );
}

export default ClientManagerModal;
